import { create } from 'zustand';
import type { Restaurant, SearchQuery } from '../types/restaurant';
import type { QueryHistoryItem } from '../../history/types/queryHistory';
import { searchRestaurants, getRestaurantDetails } from '../../../shared/services/googlePlacesService';
import { logDataSourceCheck, checkDataSource } from '../utils/dataSourceChecker';
import { getQueryHistory, saveQuery, updateQueryResultCount, getSnapshotResults, isSnapshotValid } from '../../../shared/services/queryHistoryService';
import { logger } from '../../../shared/utils/logger';

interface RestaurantState {
  restaurants: Restaurant[];
  searchQuery: SearchQuery | null;
  isLoading: boolean;
  error: string | null;
  dataSource: 'real' | 'mock' | null; // 明确标识数据源
  apiError: string | null; // API错误详情
  
  // Query history state
  queryHistory: QueryHistoryItem[];
  isLoadingHistory: boolean;
  lastSavedQueryId: string | null; // Track the last saved query for updating result count
  
  setRestaurants: (restaurants: Restaurant[]) => void;
  setSearchQuery: (query: SearchQuery) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearResults: () => void;
  loadRestaurants: (query: SearchQuery) => Promise<void>;
  
  // Query history actions
  loadHistory: () => Promise<void>;
  addToHistory: (queryText: string, metadata?: any) => Promise<void>;
  updateHistoryResultCount: (resultCount: number, resultsSnapshot?: Restaurant[] | null) => Promise<void>;
  loadFromHistory: (item: QueryHistoryItem) => Promise<void>; // Load results from history (snapshot or re-query)
}

export const useRestaurantStore = create<RestaurantState>((set, get) => ({
  restaurants: [],
  searchQuery: null,
  isLoading: false,
  error: null,
  dataSource: null, // 数据源标识: 'real' | 'mock' | null
  apiError: null, // API错误详情
  
  // Query history state
  queryHistory: [],
  isLoadingHistory: false,
  lastSavedQueryId: null,
  
  setRestaurants: (restaurants) => set({ restaurants }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearResults: () => set({ 
    restaurants: [], 
    searchQuery: null, 
    error: null, 
    dataSource: null,
    apiError: null 
  }),
  
  /**
   * Load restaurants from Google Maps API并记录数据源状态
   */
  loadRestaurants: async (query: SearchQuery) => {
    set({ 
      isLoading: true, 
      error: null, 
      searchQuery: query,
      dataSource: null,
      apiError: null
    });
    
    // Save query to history before searching
    // Note: Metadata will be saved, but language should be passed from the component
    if (query.text) {
      try {
        const metadata = {
          cuisine: query.cuisine,
          source: query.image ? 'image' : 'search', // Set source based on whether image is provided
          ...(query.metadata || {}), // Allow metadata to be passed from the component (may override source)
        };
        await get().addToHistory(query.text, metadata);
      } catch (error: any) {
        // Check if this is a query limit error
        const isLimitError = error.message && (
          error.message.includes('未注册用户最多只能保存') || 
          error.message.includes('最多只能保存') ||
          error.message.includes('请注册以保存更多查询') ||
          error.message.includes('请注册以保存')
        );
        if (isLimitError) {
          // Clear restaurants and set error state so App.tsx can handle it
          set({ 
            restaurants: [], // Clear restaurants when limit is reached
            error: error.message,
            isLoading: false,
            dataSource: null
          });
          // Re-throw the error so App.tsx can show the limit modal
          throw error;
        } else {
          // Other errors - log but continue with search
          logger.error('❌ Error saving query to history:', error);
        }
      }
    }
    
    try {
      logger.log('🔍 正在从Google Places API获取真实数据...');
      const restaurants = await searchRestaurants({
        keyword: query.text,
        budget: query.budget,
        type: query.cuisine,
      });
      
      if (restaurants.length === 0) {
        logger.warn('⚠️ Google Places API返回0个结果');
        set({ 
          restaurants: [],
          error: 'specificQueryRequired',
          isLoading: false,
          dataSource: null,
          apiError: 'ZERO_RESULTS'
        });
        
        const { lastSavedQueryId } = get();
        if (lastSavedQueryId) {
          try {
            await get().updateHistoryResultCount(0, []);
          } catch (error: any) {
            const isLimitError = error.message && (
              error.message.includes('未注册用户最多只能保存') || 
              error.message.includes('最多只能保存') ||
              error.message.includes('请注册以保存更多查询') ||
              error.message.includes('请注册以保存')
            );
            if (isLimitError) {
              set({ 
                restaurants: [],
                error: error.message,
                isLoading: false,
                dataSource: null
              });
              throw error;
            } else {
              logger.error('❌ Error updating query result count:', error);
            }
          }
        }
        return;
      }
      
      logger.log(`✅ 成功获取 ${restaurants.length} 个餐厅数据`);
      const checkResult = checkDataSource(restaurants);
      logDataSourceCheck(restaurants);
      
      logger.log(`🔍 开始获取 ${restaurants.length} 个餐厅的详细信息...`);
      const restaurantDetailsPromises = restaurants.map(async (restaurant) => {
        try {
          const details = await getRestaurantDetails(restaurant.id);
          if (details) {
            logger.log(`✅ 成功获取餐厅详情: ${restaurant.name}`);
            return {
              ...restaurant,
              ...details,
              name: details.name || restaurant.name,
              image: details.image || restaurant.image,
            };
          } else {
            logger.warn(`⚠️ 无法获取餐厅详情: ${restaurant.name}，使用原始数据`);
            return restaurant;
          }
        } catch (error: any) {
          logger.error(`❌ 获取餐厅详情失败: ${restaurant.name}`, error);
          return restaurant;
        }
      });
      
      const detailsResults = await Promise.allSettled(restaurantDetailsPromises);
      const enrichedRestaurants = detailsResults.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          logger.error(`❌ 餐厅详情获取失败 (index ${index}):`, result.reason);
          return restaurants[index];
        }
      });
      
      logger.log(`✅ 完成 ${enrichedRestaurants.length} 个餐厅的详细信息获取`);
      
      set({ 
        restaurants: enrichedRestaurants, 
        isLoading: false,
        dataSource: checkResult.isRealData ? 'real' : 'mock',
        error: checkResult.isRealData ? null : '⚠️ 数据格式异常，可能不是真实数据'
      });
      
      const { lastSavedQueryId } = get();
      if (lastSavedQueryId) {
        try {
          await get().updateHistoryResultCount(enrichedRestaurants.length, enrichedRestaurants);
        } catch (error: any) {
          const isLimitError = error.message && (
            error.message.includes('未注册用户最多只能保存') || 
            error.message.includes('最多只能保存') ||
            error.message.includes('请注册以保存更多查询') ||
            error.message.includes('请注册以保存')
          );
          if (isLimitError) {
            set({ 
              restaurants: [],
              error: error.message,
              isLoading: false,
              dataSource: null
            });
            throw error;
          } else {
            logger.error('❌ Error updating query result count:', error);
          }
        }
      }
      
      return;
    } catch (apiError: any) {
      const isLimitError = apiError.message && (
        apiError.message.includes('未注册用户最多只能保存') || 
        apiError.message.includes('最多只能保存') ||
        apiError.message.includes('请注册以保存更多查询') ||
        apiError.message.includes('请注册以保存')
      );
      
      if (isLimitError) {
        set({ 
          restaurants: [],
          error: apiError.message,
          isLoading: false,
          dataSource: null
        });
        throw apiError;
      }
      
      if (import.meta.env.DEV) {
        console.error('❌ Google Maps API错误:', apiError.message);
      }
      const errorMessage = apiError.message || '未知错误';
      set({ 
        restaurants: [],
        error: 'apiSpecificSuggestion',
        isLoading: false,
        dataSource: null,
        apiError: errorMessage
      });
      return;
    }
  },
  
  // Query history actions
  loadHistory: async () => {
    set({ isLoadingHistory: true });
    try {
      const history = await getQueryHistory(50);
      set({ queryHistory: history, isLoadingHistory: false });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading history:', error);
      }
      set({ isLoadingHistory: false });
    }
  },
  
  addToHistory: async (queryText: string, metadata?: any) => {
    try {
      logger.log('📝 addToHistory called:', { queryText, metadata });
      const { item: queryItem, error } = await saveQuery(queryText, null, metadata);
      if (queryItem) {
        logger.log('✅ Query added to history successfully:', queryItem.id);
        set((state) => ({
          queryHistory: [queryItem, ...state.queryHistory].slice(0, 50), // Keep only latest 50
          lastSavedQueryId: queryItem.id,
        }));
      } else {
        logger.warn('⚠️ saveQuery returned null, query not saved to history', error);
        // If there's an error (like query limit reached), throw it so it can be handled by the caller
        if (error) {
          logger.warn('⚠️ Error saving query:', error);
          // Check if this is a query limit error
          const isLimitError = error.includes('未注册用户最多只能保存') || 
                              error.includes('最多只能保存') ||
                              error.includes('请注册以保存更多查询') ||
                              error.includes('请注册以保存');
          if (isLimitError) {
            // Throw the error so it can be caught and handled in App.tsx
            throw new Error(error);
          }
        }
      }
    } catch (error: any) {
      logger.error('❌ Exception in addToHistory:', {
        error: error.message,
        stack: error.stack
      });
      // Re-throw limit errors so they can be handled by the caller
      if (error.message && (
        error.message.includes('未注册用户最多只能保存') || 
        error.message.includes('最多只能保存') ||
        error.message.includes('请注册以保存更多查询') ||
        error.message.includes('请注册以保存')
      )) {
        throw error;
      }
    }
  },
  
  updateHistoryResultCount: async (resultCount: number, resultsSnapshot?: Restaurant[] | null) => {
    const { lastSavedQueryId } = get();
    if (lastSavedQueryId) {
      logger.log('🔄 updateHistoryResultCount called:', {
        lastSavedQueryId,
        resultCount,
        hasSnapshot: !!resultsSnapshot,
        snapshotCount: resultsSnapshot?.length || 0
      });
      try {
        const result = await updateQueryResultCount(lastSavedQueryId, resultCount, resultsSnapshot);
        if (result.success) {
          logger.log('✅ Query result count updated successfully');
          set((state) => ({
            queryHistory: state.queryHistory.map((item) =>
              item.id === lastSavedQueryId
                ? { 
                    ...item, 
                    result_count: resultCount,
                    results_snapshot: resultsSnapshot || null,
                    snapshot_expires_at: resultsSnapshot && resultsSnapshot.length > 0
                      ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                      : null
                  }
                : item
            ),
          }));
        } else {
          logger.warn('⚠️ updateQueryResultCount returned false, update may have failed');
          if (result.error) {
            logger.warn('⚠️ Error:', result.error);
            // Check if this is a query limit error
            const isLimitError = result.error.includes('未注册用户最多只能保存') || 
                                result.error.includes('最多只能保存') ||
                                result.error.includes('请注册以保存更多查询') ||
                                result.error.includes('请注册以保存');
            if (isLimitError) {
              // Return error to be handled by the caller
              // The error will be thrown so it can be caught and handled in App.tsx
              throw new Error(result.error);
            }
          }
          logger.warn('⚠️ 请检查控制台是否有详细的错误信息（以 ❌ 开头的日志）');
          logger.warn('⚠️ 如果没有看到错误信息，可能是 RLS UPDATE 策略未配置');
          logger.warn('⚠️ 请在 Supabase Dashboard 中运行 UPDATE 策略迁移');
        }
      } catch (error: any) {
        logger.error('❌ Exception in updateHistoryResultCount:', {
          error: error.message,
          stack: error.stack
        });
        // Re-throw limit errors so they can be handled by the caller
        if (error.message && (
          error.message.includes('未注册用户最多只能保存') || 
          error.message.includes('最多只能保存') ||
          error.message.includes('请注册以保存更多查询') ||
          error.message.includes('请注册以保存')
        )) {
          throw error;
        }
      }
    } else {
      logger.warn('⚠️ Cannot update query result count: lastSavedQueryId is null');
    }
  },

  /**
   * Load results from history item
   * If snapshot is valid, use it; otherwise re-query
   */
  loadFromHistory: async (item: QueryHistoryItem) => {
    // Check if snapshot is available and valid
    const snapshotResults = getSnapshotResults(item);
    
    if (snapshotResults && snapshotResults.length > 0) {
      // Use snapshot for instant loading
      logger.log(`📸 从快照加载 ${snapshotResults.length} 个餐厅结果`);
      set({
        restaurants: snapshotResults,
        searchQuery: { text: item.query_text, metadata: item.metadata || {} },
        isLoading: false,
        error: null,
        dataSource: 'real', // Snapshot is from real data
      });
      return;
    }

    // Snapshot expired or not available, re-query
    logger.log('🔄 快照已过期或不存在，重新查询...');
    const query: SearchQuery = {
      text: item.query_text,
      metadata: item.metadata || {},
    };
    
    // Extract metadata for query
    if (item.metadata) {
      if (item.metadata.cuisine) {
        query.cuisine = item.metadata.cuisine;
      }
    }
    
    await get().loadRestaurants(query);
  },
}));
